import React, { useEffect, useState, useRef } from "react";
import Track from "../../components/trackComponents/Track";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyles";
import { SiteName } from "../../global/SiteName";
import {
  FaShippingFast,
  FaHeadset,
  FaBusinessTime,
  FaMapMarkerAlt,
  FaClock,
  FaShieldAlt,
  FaGlobeAmericas,
  FaWarehouse,
  FaTruckLoading,
} from "react-icons/fa";
import Footer from "../../components/footers/Footer";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const HomePage = () => {
  const [counterKey, setCounterKey] = useState(0);
  const statsRef = useRef(null);
  const [statsInView, setStatsInView] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out",
      once: false,
      offset: 100,
    });

    // Refresh AOS on route change
    return () => {
      AOS.refresh();
    };
  }, []);

  // Intersection Observer for stats section
  const { ref: statsRefInView, inView: isStatsInView } = useInView({
    threshold: 0.3,
    triggerOnce: false, // Allow multiple triggers
  });

  // Reset counter key when stats come into view
  useEffect(() => {
    if (isStatsInView) {
      setCounterKey((prevKey) => prevKey + 1);
    }
  }, [isStatsInView]);

  // Stats configuration
  const statsData = [
    {
      id: 1,
      value: 150,
      suffix: "+",
      duration: 2.5,
      description: "Countries Served",
      color: "text-primary dark:text-primary",
    },
    {
      id: 2,
      value: 99.7,
      suffix: "%",
      duration: 3,
      decimals: 1,
      description: "On-Time Delivery",
      color: "text-secondary dark:text-secondary",
    },
    {
      id: 3,
      value: 24,
      suffix: "/7",
      duration: 2,
      description: "Operational Support",
      color: "text-primary dark:text-primary",
    },
    {
      id: 4,
      value: 10,
      suffix: "M+",
      duration: 2.5,
      description: "Annual Shipments",
      color: "text-secondary dark:text-secondary",
    },
  ];

  return (
    <div
      className={`w-full flex flex-col bg-light dark:bg-dark ${CommonClasses.transition}`}
    >
      <Track options={true} />

      {/* Hero Section */}
      <div
        className={`w-full py-20 bg-gradient-to-b from-primary/30 to-light dark:from-primary/15 dark:to-dark ${CommonClasses.transition}`}
      >
        <div className="container mx-auto px-4 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-primary mb-6"
            data-aos="fade-up"
          >
            Revolutionizing Global Logistics with Precision and Innovation
          </h1>
          <p
            className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            {SiteName} stands at the forefront of supply chain innovation,
            orchestrating seamless end-to-end logistics solutions that transcend
            geographical boundaries and operational complexities.
          </p>
        </div>
      </div>

      {/* Services Section */}
      <div className={`w-full py-20`}>
        <div className="container mx-auto px-4">
          <h2
            className={`${GlobalStyles.headerPrimary} mb-12 text-center`}
            data-aos="fade-up"
          >
            Comprehensive Logistics Solutions
          </h2>
          <p
            className="text-center text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-16"
            data-aos="fade-up"
            data-aos-delay="50"
          >
            Our integrated service portfolio delivers operational excellence
            through technologically advanced infrastructure and
            industry-specific expertise.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div
              className={`${GlobalStyles.serviceCard}`}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className={`${GlobalStyles.serviceIcon}`}>
                <FaShippingFast className="text-4xl" />
              </div>
              <h3 className={`${GlobalStyles.serviceTitle}`}>
                Expedited Global Distribution
              </h3>
              <p className={`${GlobalStyles.text} text-sm leading-relaxed`}>
                Leverage our optimized international network featuring
                time-definite deliveries, customs clearance facilitation, and
                multi-modal transportation orchestration to accelerate your
                global market penetration.
              </p>
            </div>

            <div
              className={`${GlobalStyles.serviceCard} bg-secondary/90`}
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className={`${GlobalStyles.serviceIcon} text-secondary`}>
                <FaBusinessTime className="text-4xl" />
              </div>
              <h3 className={`${GlobalStyles.serviceTitle}`}>
                Enterprise Supply Chain Integration
              </h3>
              <p className={`${GlobalStyles.text} text-sm leading-relaxed`}>
                We architect bespoke logistics ecosystems featuring inventory
                optimization algorithms, predictive analytics, and scalable
                infrastructure to elevate your operational throughput and
                competitive positioning.
              </p>
            </div>

            <div
              className={`${GlobalStyles.serviceCard}`}
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className={`${GlobalStyles.serviceIcon}`}>
                <FaHeadset className="text-4xl" />
              </div>
              <h3 className={`${GlobalStyles.serviceTitle}`}>
                Dedicated Logistics Concierge
              </h3>
              <p className={`${GlobalStyles.text} text-sm leading-relaxed`}>
                Our specialized support teams provide proactive monitoring,
                crisis management protocols, and strategic consultation to
                ensure uninterrupted logistics operations across all time zones.
              </p>
            </div>
          </div>

          {/* Additional Services Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-16">
            <div
              className="p-8 rounded-xl bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
              data-aos="zoom-in"
              data-aos-delay="100"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-xl mb-6">
                <FaWarehouse className="text-2xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Smart Warehousing
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Automated storage systems with real-time inventory visibility
                and climate-controlled facilities for specialized commodities.
              </p>
            </div>

            <div
              className="p-8 rounded-xl bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-secondary/10 rounded-xl mb-6">
                <FaTruckLoading className="text-2xl text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Last-Mile Optimization
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Route intelligence algorithms and dynamic scheduling for maximum
                delivery efficiency in urban and remote environments.
              </p>
            </div>

            <div
              className="p-8 rounded-xl bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
              data-aos="zoom-in"
              data-aos-delay="300"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-xl mb-6">
                <FaGlobeAmericas className="text-2xl text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Cross-Border Expertise
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Regulatory compliance management and documentation processing
                for seamless international trade across 150+ countries.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className={`w-full py-20 bg-gray-50 dark:bg-gray-900/50`}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2
              className={`${GlobalStyles.headerPrimary} mb-6`}
              data-aos="fade-up"
            >
              Operational Excellence Through Innovation
            </h2>
            <p
              className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg"
              data-aos="fade-up"
              data-aos-delay="50"
            >
              {SiteName} synthesizes cutting-edge technology with industry
              expertise to deliver unparalleled reliability in global logistics
              management.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div
              className="flex flex-col items-center text-center p-8 rounded-2xl bg-white dark:bg-gray-800 shadow-xl hover:shadow-2xl transition-shadow duration-300"
              data-aos="flip-left"
              data-aos-delay="100"
            >
              <div className={`${GlobalStyles.serviceIcon} mb-6`}>
                <FaMapMarkerAlt className="text-4xl" />
              </div>
              <h3
                className={`${GlobalStyles.serviceTitle} mb-4 text-primary dark:text-primary`}
              >
                Predictive Supply Chain Intelligence
              </h3>
              <p
                className={`text-primary/90 dark:text-primary/80 text-sm leading-relaxed`}
              >
                Our proprietary tracking ecosystem employs IoT sensors and
                machine learning algorithms to provide predictive analytics,
                disruption forecasting, and autonomous contingency planning
                throughout your shipment's journey.
              </p>
            </div>

            <div
              className="flex flex-col items-center text-center p-8 rounded-2xl bg-white dark:bg-gray-800 shadow-xl hover:shadow-2xl transition-shadow duration-300"
              data-aos="flip-left"
              data-aos-delay="200"
            >
              <div className={`${GlobalStyles.serviceIcon} mb-6`}>
                <FaClock className="text-4xl" />
              </div>
              <h3
                className={`${GlobalStyles.serviceTitle} mb-4 text-primary dark:text-primary`}
              >
                Precision Scheduling Architecture
              </h3>
              <p
                className={`text-primary/90 dark:text-primary/80 text-sm leading-relaxed`}
              >
                We implement synchronized logistics protocols that harmonize
                transportation nodes, warehouse operations, and delivery windows
                to achieve 99.7% on-time delivery performance across our
                network.
              </p>
            </div>

            <div
              className="flex flex-col items-center text-center p-8 rounded-2xl bg-white dark:bg-gray-800 shadow-xl hover:shadow-2xl transition-shadow duration-300"
              data-aos="flip-left"
              data-aos-delay="300"
            >
              <div className={`${GlobalStyles.serviceIcon} mb-6`}>
                <FaShieldAlt className="text-4xl" />
              </div>
              <h3
                className={`${GlobalStyles.serviceTitle} mb-4 text-primary dark:text-primary`}
              >
                Multi-Layered Security Protocol
              </h3>
              <p
                className={`text-primary/90 dark:text-primary/80 text-sm leading-relaxed`}
              >
                Your assets are protected by comprehensive risk mitigation
                strategies including tamper-evident packaging, GPS-enabled
                surveillance, temperature-controlled environments, and
                blockchain-verified chain-of-custody documentation.
              </p>
            </div>
          </div>

          {/* Stats Section */}
          <div
            ref={statsRefInView}
            className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
            data-aos="fade-up"
            data-aos-delay="400"
            key={`stats-container-${counterKey}`}
          >
            {statsData.map((stat) => (
              <div
                key={`${stat.id}-${counterKey}`}
                className="p-6"
                data-aos="zoom-in"
                data-aos-delay={100 * stat.id}
              >
                <div
                  className={`text-4xl md:text-5xl font-bold ${stat.color} mb-2`}
                >
                  {isStatsInView ? (
                    <CountUp
                      key={`${stat.id}-countup-${counterKey}`}
                      start={0}
                      end={stat.value}
                      duration={stat.duration}
                      suffix={stat.suffix}
                      decimals={stat.decimals || 0}
                      separator=","
                      delay={0.2}
                      onStart={() => console.log("Started counting:", stat.id)}
                      onEnd={() => console.log("Finished counting:", stat.id)}
                    />
                  ) : (
                    <span>0{stat.suffix}</span>
                  )}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div
        className="w-full py-16 bg-gradient-to-r from-primary/10 to-secondary/10 dark:from-primary/5 dark:to-secondary/5"
        data-aos="fade-up"
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-primary dark:text-white mb-6">
            Ready to Optimize Your Logistics?
          </h2>
          <p className="text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto mb-10">
            Join thousands of satisfied clients who trust {SiteName} for their
            logistics needs. Experience the difference today.
          </p>
          <button className="px-8 py-3 bg-primary text-white font-bold rounded-lg hover:bg-primary/90 transition-colors duration-300 shadow-lg hover:shadow-xl">
            Get Started Now
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;
